import { useState } from "react";
import { api } from "@/lib/api";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Flame } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-12">
      <form onSubmit={submit} className="w-full max-w-md border border-[#2A2A2A] bg-[#121212] p-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 grid place-items-center bg-[#FF4500] text-black"><Flame className="h-5 w-5" /></div>
          <div className="font-display text-2xl font-bold tracking-tighter">RESET PASSWORD</div>
        </div>
        {sent ? (
          <div className="space-y-4">
            <p className="font-mono text-sm text-gray-300 leading-relaxed">If an account exists for that email, we sent a reset link. Check your inbox (and spam folder) — it expires in 1 hour.</p>
            <Link to="/login"><Button data-testid="back-to-login" className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 mt-2">Back to sign in</Button></Link>
          </div>
        ) : (<>
          <p className="font-mono text-xs text-gray-400 leading-relaxed mb-4">Enter your email and we'll send you a reset link.</p>
          <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Email</Label>
          <Input data-testid="fp-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
          <Button data-testid="fp-submit" type="submit" disabled={busy}
                  className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 mt-6">
            {busy ? "Sending..." : "Email me a reset link"}
          </Button>
          <div className="mt-6 text-center font-mono text-xs text-gray-500">
            Remembered it? <Link to="/login" className="text-[#FF4500] hover:brightness-125">Sign in</Link>
          </div>
        </>)}
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match");
    if (pw.length < 6) return toast.error("At least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated · sign in with the new one");
      nav("/login");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-12">
      <form onSubmit={submit} className="w-full max-w-md border border-[#2A2A2A] bg-[#121212] p-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 grid place-items-center bg-[#FF4500] text-black"><Flame className="h-5 w-5" /></div>
          <div className="font-display text-2xl font-bold tracking-tighter">SET NEW PASSWORD</div>
        </div>
        <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">New password (min 6)</Label>
        <Input data-testid="rp-new" required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
               className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
        <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Confirm password</Label>
        <Input data-testid="rp-confirm" required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
               className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
        <Button data-testid="rp-submit" type="submit" disabled={busy || !token}
                className="w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 mt-6">
          {busy ? "Updating..." : "Update password"}
        </Button>
        {!token && <p className="font-mono text-xs text-yellow-500 mt-4 text-center">No reset token in URL — request a new link.</p>}
      </form>
    </div>
  );
}
